import { useState } from "react";
import { Phone, EnvelopeSimple, MapPin, Clock, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    "Facial Balancing",
    "Injectable Treatments",
    "Skin Rejuvenation",
    "Body Contouring",
    "Skincare Consultation",
    "VIP Membership Inquiry",
    "Other",
  ];

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
              Get In Touch
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.9] text-matte">
              Begin Your
              <br />
              <span className="italic">Journey</span>
            </h1>
            <p className="mt-8 text-base md:text-lg font-light text-matte/70 leading-relaxed max-w-xl">
              Schedule your consultation with Medical Aesthetic Arts LLC and take the first step toward your 
              aesthetic goals. Our team is here to guide you every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Form */}
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-matte mb-8">
                Request a Consultation
              </h2>
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="space-y-6"
              >
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-matte/60 mb-2 block">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="input-name"
                    className="w-full line-input"
                    placeholder="Your name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-matte/60 mb-2 block">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="input-email"
                      className="w-full line-input"
                      placeholder="you@email.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-matte/60 mb-2 block">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="input-phone"
                      className="w-full line-input"
                      placeholder="(844) 943-6191"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-matte/60 mb-2 block">
                    Service Interest
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    data-testid="input-service"
                    className="w-full line-input appearance-none cursor-pointer"
                  >
                    <option value="">Select a service...</option>
                    {services.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-matte/60 mb-2 block">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    data-testid="input-message"
                    className="w-full line-input resize-none"
                    placeholder="Tell us about your goals..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="submit-btn"
                  disabled={isSubmitting}
                  className="btn-primary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending..." : "Send Inquiry"}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-matte mb-8">
                Contact Information
              </h2>

              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="text-taupe">
                    <Globe size={20} weight="light" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-matte mb-1">Website</h4>
                    <a
                      href="https://aestheticmedicalarts.online"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-matte/60 hover:text-taupe transition-colors"
                    >
                      aestheticmedicalarts.online
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-taupe">
                    <Phone size={20} weight="light" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-matte mb-1">Phone</h4>
                    <a
                      href="tel:+18449436191"
                      className="text-matte/60 hover:text-taupe transition-colors"
                    >
                      (844) 943-6191
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-taupe">
                    <EnvelopeSimple size={20} weight="light" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-matte mb-1">Email</h4>
                    <a
                      href="mailto:info@aestheticmedicalarts.online"
                      className="text-matte/60 hover:text-taupe transition-colors"
                    >
                      info@aestheticmedicalarts.online
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-taupe">
                    <Clock size={20} weight="light" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-matte mb-1">Hours of Operation</h4>
                    <div className="text-matte/60 text-sm space-y-1">
                      <p>Monday - Friday: 9:00 AM - 5:00 PM</p>
                      <p>Saturday - Sunday: 11:00 AM - 4:00 PM</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-taupe">
                    <MapPin size={20} weight="light" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-matte mb-1">Main Office</h4>
                    <p className="text-matte/60">
                      3100 Smoketree Ct, Ste 800
                      <br />
                      Raleigh, NC 27604
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-taupe">
                    <MapPin size={20} weight="light" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-matte mb-1">Headquarters</h4>
                    <p className="text-matte/60">
                      1950 Bluegrass Cir, Ste 360
                      <br />
                      Cheyenne, WY 82001
                    </p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-12 aspect-video bg-cream border border-matte/10 flex items-center justify-center">
                <div className="text-center">
                  <MapPin size={32} weight="light" className="text-taupe mx-auto mb-3" />
                  <p className="text-sm text-matte/50">Raleigh, NC & Cheyenne, WY</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
              What to Expect
            </h2>
            <p className="mt-6 text-base font-light text-matte/60">
              Your consultation with Medical Aesthetic Arts LLC begins with a comprehensive discussion of your 
              aesthetic goals, followed by a personalized treatment plan designed 
              specifically for you.
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <span className="inline-block font-serif text-3xl text-taupe mb-4">01</span>
                <h4 className="font-serif text-lg text-matte mb-2">Consultation</h4>
                <p className="text-sm font-light text-matte/50">
                  Discuss your goals and concerns with our expert team.
                </p>
              </div>
              <div className="text-center">
                <span className="inline-block font-serif text-3xl text-taupe mb-4">02</span>
                <h4 className="font-serif text-lg text-matte mb-2">Analysis</h4>
                <p className="text-sm font-light text-matte/50">
                  Comprehensive facial analysis and skin assessment.
                </p>
              </div>
              <div className="text-center">
                <span className="inline-block font-serif text-3xl text-taupe mb-4">03</span>
                <h4 className="font-serif text-lg text-matte mb-2">Your Plan</h4>
                <p className="text-sm font-light text-matte/50">
                  Receive a customized treatment plan tailored to you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
